import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';

function resolveMediaUrl(url) {
  const u = (url || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) {
    const base = String(API_BASE_URL || '').replace(/\/api\/?$/, '');
    return `${base}${u}`;
  }
  return u;
}

function youtubeId(url) {
  const m = String(url || '').match(
    /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|)([\w-]{11})/
  );
  return m ? m[1] : null;
}

export default function ShareLanding({
  token,
  isLoggedIn,
  autoImport = false,
  onClose,
  onRequestLogin,
  onImported,
}) {
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [importReport, setImportReport] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setImportReport(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/share-links/${encodeURIComponent(token)}`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          throw new Error(json.message || 'Link non valido');
        }
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Errore caricamento condivisione');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleImport = async () => {
    if (!isLoggedIn) {
      onRequestLogin?.();
      return;
    }
    setImporting(true);
    setError('');
    try {
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/share-links/${encodeURIComponent(token)}/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Import fallito');
      }
      setImportReport(json.report || null);
      onImported?.(json);
    } catch (err) {
      setError(err.message || 'Import fallito');
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    if (!autoImport || !isLoggedIn || loading || !data || importing || importReport) return;
    handleImport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoImport, isLoggedIn, loading, data]);

  const snapshot = data?.snapshot || {};
  const songs = Array.isArray(snapshot.songs) ? snapshot.songs : [];
  const choreos = Array.isArray(snapshot.choreographies) ? snapshot.choreographies : [];
  const associations = Array.isArray(snapshot.associations) ? snapshot.associations : [];

  return (
    <div className="min-h-screen bg-gray-100 px-3 py-6 dark:bg-gray-900 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">Condivisione</h1>
            {data && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Da <strong>{data.sharedBy}</strong>
                {data.expiresAt ? ` · valido fino al ${new Date(data.expiresAt).toLocaleDateString()}` : ''}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              Chiudi
            </button>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-500" />
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-100 px-3 py-2 text-sm font-semibold text-red-800 dark:border-red-700 dark:bg-red-900/50 dark:text-red-100">
            {error}
          </div>
        )}

        {!loading && data && (
          <>
            <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/80 p-3 dark:border-indigo-700 dark:bg-indigo-950/40">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Qui puoi <strong>solo vedere</strong> i contenuti e aprire i link.
                {isLoggedIn
                  ? ' Con il tuo account puoi anche importarli nella tua libreria.'
                  : ' Per importarli serve un account: ti chiederemo di accedere o registrarti.'}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={importing}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold text-white ${importing ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                >
                  {isLoggedIn
                    ? (importing ? 'Importo…' : 'Importa nella mia libreria')
                    : 'Accedi / registrati per importare'}
                </button>
              </div>
              {importReport && (
                <p className="mt-2 text-xs text-emerald-800 dark:text-emerald-200">
                  Importati: C {importReport?.copied?.songs || 0} · Co {importReport?.copied?.choreographies || 0} · A {importReport?.copied?.associations || 0}
                  {(importReport?.skipped?.songs || importReport?.skipped?.choreographies || importReport?.skipped?.associations)
                    ? ` · saltati: C ${importReport?.skipped?.songs || 0} · Co ${importReport?.skipped?.choreographies || 0} · A ${importReport?.skipped?.associations || 0}`
                    : ''}
                </p>
              )}
            </div>

            {songs.length > 0 && (
              <section className="mb-4 rounded-xl bg-white p-3 shadow-md dark:bg-gray-800">
                <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Canzoni ({songs.length})</h2>
                <ul className="space-y-2">
                  {songs.map((song) => {
                    const href = resolveMediaUrl(song.url);
                    return (
                      <li key={song.id} className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{song.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{song.artist || '—'}</p>
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-block text-xs font-semibold text-indigo-600 dark:text-indigo-300"
                          >
                            Apri media
                          </a>
                        ) : (
                          <p className="mt-1 text-xs text-gray-400">Nessun link</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {choreos.length > 0 && (
              <section className="mb-4 rounded-xl bg-white p-3 shadow-md dark:bg-gray-800">
                <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Coreografie ({choreos.length})</h2>
                <ul className="space-y-3">
                  {choreos.map((choreo) => (
                    <li key={choreo.id} className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{choreo.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {[choreo.artist, choreo.difficulty].filter(Boolean).join(' · ') || '—'}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(choreo.media || []).map((media, idx) => {
                          const href = resolveMediaUrl(media.url);
                          const yt = youtubeId(media.url);
                          const label = (media.label || '').trim() || `Media ${idx + 1}`;
                          if (!href) return null;
                          return (
                            <a
                              key={`${choreo.id}-${media.id || idx}`}
                              href={yt ? `https://www.youtube.com/watch?v=${yt}` : href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
                            >
                              {label}
                            </a>
                          );
                        })}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {associations.length > 0 && (
              <section className="mb-4 rounded-xl bg-white p-3 shadow-md dark:bg-gray-800">
                <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Associazioni ({associations.length})</h2>
                <ul className="space-y-2">
                  {associations.map((assoc) => {
                    const song = songs.find((s) => String(s.id) === String(assoc.songId));
                    const choreo = choreos.find((c) => String(c.id) === String(assoc.choreographyId));
                    return (
                      <li key={assoc.id} className="rounded-lg border border-gray-200 p-2.5 text-sm text-gray-800 dark:border-gray-700 dark:text-gray-200">
                        {(song && song.title) || 'Canzone'} → {(choreo && choreo.title) || 'Coreografia'}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
